'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes as getDataAttributesFromProps,
} from '@wix/editor-elements-common-utils';
import type { ISelectionTagsListProps } from '../SelectionTagsList.types';
import SelectionTag from '../../SelectionTag/viewer/SelectionTag';
import semanticClassNames from '../SelectionTagsList.semanticClassNames';
import { st, classes } from './style/SelectionTagsList.st.css';
import { TestIds } from './constants';

const getDataAttributes = (props: ISelectionTagsListProps) => {
  const keys = Object.keys(props) as Array<keyof ISelectionTagsListProps>;

  return keys.reduce(
    (acc, key) =>
      key.startsWith('data-') ? { ...acc, [key]: props[key] } : acc,
    {} as Record<string, unknown>,
  );
};

export const SelectionTagsList: React.FC<ISelectionTagsListProps> = props => {
  const {
    id,
    className,
    customClassNames = [],
    onChange,
    legend,
    value: values,
    label: inputLabel,
    options,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onDblClick,
    isDisabled,
    shouldShowValidityIndication,
    isValid,
    validateValueAndShowIndication,
    required,
    isClassNameToRootEnabled,
    ariaAttributes,
    lang,
  } = props;

  const styleState = {
    disabled: isDisabled,
    error: Boolean(shouldShowValidityIndication && !isValid),
  };

  const a11yProps = {
    ...((legend || ariaAttributes?.label) && {
      'aria-label': ariaAttributes?.label || legend,
    }),
    ...(inputLabel && { 'aria-labelledby': `input_${id}` }),
  };

  const dataAttr = getDataAttributes(props);

  const handleChange = (ev: React.FormEvent<HTMLInputElement>) => {
    const { value: targetValue, checked } = ev.target as HTMLInputElement;

    const nextValue = checked
      ? [...values, targetValue]
      : values?.filter(value => value !== targetValue);

    onChange(nextValue);
    validateValueAndShowIndication?.({ value: nextValue });
  };

  return (
    <div
      id={id}
      {...dataAttr}
      className={st(
        classes.root,
        { disabled: isDisabled },
        isClassNameToRootEnabled ? className : '',
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      {...(!isDisabled && {
        onMouseEnter,
        onMouseLeave,
        onClick,
        onDoubleClick: onDblClick,
      })}
      lang={lang}
    >
      <div
        className={classes.tagList}
        data-testid={TestIds.tagsList}
        {...a11yProps}
        role="group"
      >
        {inputLabel && (
          <div
            id={`input_${id}`}
            className={classNames(classes.label, {
              [classes.requiredIndication]: required,
            })}
          >
            {inputLabel}
          </div>
        )}
        {options?.map(({ value, label, link, rel }, index) => {
          const isChecked = values?.includes(value!);
          const tagId = `${value || label}-${id}-${index}`;
          const tagClassName = st(
            classes.tag,
            {
              ...styleState,
              selected: isChecked,
            },
            !isClassNameToRootEnabled ? className : '',
          );

          return (
            <SelectionTag
              {...getDataAttributesFromProps(props)}
              key={tagId}
              id={tagId}
              onChange={handleChange}
              label={label}
              value={value}
              checked={isChecked}
              className={st(
                tagClassName,
                formatClassNames(semanticClassNames.selectionsTagsOption),
              )}
              disabled={isDisabled}
              link={link}
              rel={rel}
              required={required}
            >
              {label}
            </SelectionTag>
          );
        })}
      </div>
    </div>
  );
};

export default SelectionTagsList;
